import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SideMenu from "../../component/SideMenu";
import Topbar from "../../component/Topbar";
import axios from 'axios';
import Swal from 'sweetalert2';
import { baseurl, imgurl } from '../../baseurl';
import { 
  ArrowLeft, Upload, FileJson, CheckCircle, 
  MapPin, User, AlertCircle, Eye, Download, History, Clock, HelpCircle
} from 'lucide-react';
import AddZones from './AddZones';
import MarkComplete from './MarkComplete';

const UploadData = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [submission, setSubmission] = useState(null);
  const [targetPackage, setTargetPackage] = useState(null);
  const [existingFiles, setExistingFiles] = useState({});
  const [uploadHistory, setUploadHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geoFiles, setGeoFiles] = useState({});

  const adminToken = localStorage.getItem('adminToken');

  useEffect(() => {
    fetchInitialData();
  }, [id]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [subRes, pkgRes, filesRes] = await Promise.all([
        axios.get(`${baseurl}getSubmissionByIdAdmin/${id}`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${baseurl}getAllServicePackages`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        }),
        axios.get(`${baseurl}getProjectFiles/${id}`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        })
      ]);

      if (subRes.data.success) {
        const subData = subRes.data.data;
        const reportType = subData?.project_data?.reportType;
        
        setSubmission(subData);

        // Try to match package
        let matchedPkg = pkgRes.data.data?.find(p => 
          p.name?.toLowerCase().trim() === reportType?.toLowerCase().trim()
        );

        // LOGIC FIX: If no match, create a fallback package using existing file keys
        // This ensures the UI doesn't break and you can still see uploaded layers
        if (!matchedPkg) {
          const discoveredFeatures = filesRes.data.success ? filesRes.data.data.map(f => f.feature_name) : [];
          matchedPkg = {
            name: reportType || "Unknown",
            features: discoveredFeatures.length > 0 ? discoveredFeatures : ["HardEdge", "PropertyLine", "Building"], // Default common features
            isMismatch: true
          };
        }
        
        setTargetPackage(matchedPkg);

        if (filesRes.data.success) {
          const fileMap = {};
          (filesRes.data.data || []).forEach(file => {
            fileMap[file.feature_name] = file;
          });
          setExistingFiles(fileMap);
          setUploadHistory(filesRes.data.history || []);
        }
      }
    } catch (err) {
      console.error("Fetch error:", err);
      Swal.fire('Error', 'Failed to synchronize project data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (featureName, file) => {
    if (file && !file.name.endsWith('.json') && !file.name.endsWith('.geojson')) {
      return Swal.fire('Invalid File', 'Please upload only GeoJSON files', 'warning');
    }
    setGeoFiles(prev => ({ ...prev, [featureName]: file }));
  };

  const handleFinalSubmit = async () => {
    const filesToUpload = Object.keys(geoFiles).length;
    if (filesToUpload === 0) return Swal.fire('No Changes', 'Select a file first', 'info');

    Swal.fire({ title: 'Uploading...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
      const formData = new FormData();
      formData.append('submissionId', id);
      Object.entries(geoFiles).forEach(([feature, file]) => {
        formData.append(`file_${feature}`, file);
      });

      const res = await axios.post(`${baseurl}submitProjectDelivery`, formData, {
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.success) {
        Swal.fire('Success', 'Layers updated', 'success');
        setGeoFiles({});
        fetchInitialData();
      }
    } catch (err) {
      Swal.fire('Error', 'Upload failed', 'error');
    }
  };

  if (loading) return <div className="p-5 text-center text-primary fw-bold">Loading...</div>;
  if (!submission) return <div className="p-5 text-center">Project ID {id} not found in database.</div>;

  return (
    <div className="app-container">
      <Topbar />
      <div className="main-wrapper" style={{ background: '#F4F7FE', display: 'flex', minHeight: '100vh' }}>
        <div className="profile-content-container p-4" style={{ flex: 1 }}>
          
          {/* Header */}
          <div className="d-flex align-items-center mb-4">
            <button onClick={() => navigate(-1)} className="btn btn-white shadow-sm rounded-circle me-3">
              <ArrowLeft size={20} />
            </button>
            <div className="me-auto">
              <h3 className="fw-bold m-0" style={{ color: '#1B2559' }}>Project Delivery</h3>
              <p className="text-muted small m-0">
                Type: <span className="text-primary fw-bold">{submission.project_data?.reportType}</span> 
                {targetPackage.isMismatch && <span className="badge bg-danger-soft text-danger ms-2 small">Package Mismatch</span>}
              </p>
            </div>

            {!submission.complete_at ? <MarkComplete submissionId={id} /> : (
              <div className="text-success fw-bold small me-3"><CheckCircle size={16} /> Completed</div>
            )}
            <AddZones submissionId={id}/>
            <button className={`btn btn-primary rounded-pill px-4 ms-2 ${Object.keys(geoFiles).length === 0 ? 'disabled' : ''}`} onClick={handleFinalSubmit}>
              <Upload size={18} className="me-2" /> Save Layers
            </button>
          </div>

          {/* Mismatch Warning Alert */}
          {targetPackage.isMismatch && (
            <div className="alert alert-warning border-0 shadow-sm rounded-4 d-flex align-items-center mb-4">
              <AlertCircle className="me-3" />
              <div>
                <div className="fw-bold">Configuration Mismatch</div>
                <div className="small opacity-75">The report type "<strong>{submission.project_data?.reportType}</strong>" isn't defined in Service Packages. Showing existing layers and defaults.</div>
              </div>
            </div>
          )}

          <div className="row g-4">
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm p-4 mb-4" style={{ borderRadius: '20px' }}>
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <h5 className="fw-bold m-0">Feature Layers</h5>
                  <span className="badge bg-light text-primary rounded-pill px-3 py-2">
                    {Object.keys(existingFiles).length} Total Layers
                  </span>
                </div>

                <div className="row g-3">
                  {targetPackage.features.map((feature, index) => {
                    const existing = existingFiles[feature];
                    const pending = geoFiles[feature];
                    return (
                      <div key={index} className="col-md-12">
                        <div className={`p-3 border rounded-4 d-flex align-items-center bg-white ${pending ? 'border-primary' : ''}`}>
                          <div className="flex-grow-1 overflow-hidden">
                            <span className="fw-bold small text-uppercase d-block">{feature}</span>
                            <div className="text-muted small text-truncate">
                                {pending ? <span className="text-primary fw-bold">Pending: {pending.name}</span> : 
                                 existing ? <span className="text-success opacity-75">Live: {existing.file_path.split('/').pop()}</span> : 
                                 'No file'}
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            {existing && (
                                <>
                                    <a href={`${imgurl}${existing.file_path}`} target="_blank" rel="noreferrer" className="btn btn-sm btn-light rounded-pill"><Eye size={14} /></a>
                                    <a href={`${imgurl}${existing.file_path}`} download className="btn btn-sm btn-light rounded-pill"><Download size={14} /></a>
                                </>
                            )}
                            <label className="btn btn-sm btn-outline-primary rounded-pill px-3" style={{ cursor: 'pointer' }}>
                                {pending ? 'Change' : 'Upload'}
                                <input type="file" hidden accept=".json,.geojson" onChange={(e) => handleFileChange(feature, e.target.files[0])} />
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* History */}
              <div className="card border-0 shadow-sm p-4" style={{ borderRadius: '20px' }}>
                <h6 className="fw-bold mb-3"><History size={18} className="me-2"/>Activity History</h6>
                <div className="table-responsive">
                    <table className="table table-borderless align-middle small">
                        <thead><tr className="text-muted"><th>Action</th><th>Feature</th><th>Date</th><th>Files</th></tr></thead>
                        <tbody>
                            {uploadHistory.map((log, i) => (
                                <tr key={i} className="border-top">
                                    <td><span className="badge bg-primary-soft text-primary">{log.action_type}</span></td>
                                    <td className="fw-bold">{log.feature_name}</td>
                                    <td>{new Date(log.changed_at).toLocaleDateString()}</td>
                                    <td><a href={`${imgurl}${log.new_file_path}`} target="_blank" rel="noreferrer">View</a></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card border-0 shadow-sm p-4 mb-4" style={{ borderRadius: '20px' }}>
                <h6 className="fw-bold text-primary mb-3">Client Information</h6>
                <div className="d-flex align-items-center mb-3">
                  <div className="avatar-small me-3"><User size={18} /></div>
                  <div>
                    <div className="fw-bold small">{submission.full_name}</div>
                    <div className="text-muted small">{submission.email}</div>
                  </div>
                </div>
                <div className="small p-3 bg-light rounded-3">
                   <div className="mb-1"><MapPin size={12}/> <strong>{submission.project_data?.address}</strong></div>
                   <div className="opacity-75">ID: {submission.property_id}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .bg-primary-soft { background: #EBEBFF; color: #4318FF; }
        .bg-danger-soft { background: #FFEBEB; color: #FF4343; }
        .avatar-small { width: 35px; height: 35px; background: #F4F7FE; color: #4318FF; display: flex; align-items: center; justify-content: center; border-radius: 8px; }
        .btn-white { background: white; border: none; }
      `}</style>
    </div>
  );
};

export default UploadData;